import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Check, X, Trophy, Star } from "lucide-react";
import { toast } from "sonner";

/**
 * Movie-Trivia-Widget für die Landing-Page.
 * Eingeloggte Club-Mitglieder erhalten Punkte für richtige Antworten.
 * Frage rotiert deterministisch pro Kalendertag ("Frage des Tages").
 */
export default function TriviaWidget() {
  const [q, setQ] = useState(null);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [answered, setAnswered] = useState(() => {
    try { return localStorage.getItem("trivia-answered-today") === new Date().toDateString(); } catch { return false; }
  });

  useEffect(() => {
    api.get("/trivia/daily").then((r) => setQ(r.data)).catch(() => setQ(null));
  }, []);

  const submit = async (idx) => {
    if (selected !== null || answered) return;
    setSelected(idx);
    try {
      const { data } = await api.post("/trivia/answer", { question_id: q.id, selected_index: idx });
      setResult(data);
      if (data.awarded) toast.success(`+${data.points} Punkte gutgeschrieben!`);
      try {
        localStorage.setItem("trivia-answered-today", new Date().toDateString());
        setAnswered(true);
      } catch { /* ignore quota errors */ }
    } catch (e) {
      setSelected(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  if (!q) return null;

  return (
    <section id="trivia" className="relative py-20 px-6 md:px-14 border-t border-[#1A1A1A] bg-[#080604]" data-testid="landing-trivia-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">
            <Sparkles size={12} fill="#D4AF37" strokeWidth={0} /> Frage des Tages
          </div>
          <h2 className="font-heading text-3xl md:text-5xl mt-3">Film-Trivia</h2>
          <p className="text-[#A8A398] text-sm mt-3 max-w-lg mx-auto">Wie gut kennst du das Kino? Beantworte die Frage und sammle Punkte für dein Club-Konto.</p>
        </div>

        <div className="card-lux p-6 md:p-8 relative overflow-hidden">
          {/* Meta */}
          <div className="flex items-center gap-3 mb-5 text-[10px] uppercase tracking-[0.32em]">
            <span className="text-[#D4AF37]">{q.category}</span>
            <span className="text-[#575148]">·</span>
            <span className="text-[#A8A398]">{q.difficulty}</span>
            <span className="text-[#575148]">·</span>
            <span className="text-[#EEDC82] flex items-center gap-1"><Star size={10} fill="#EEDC82" strokeWidth={0} /> {q.points} Punkte</span>
          </div>

          <h3 className="font-heading text-2xl md:text-3xl leading-tight mb-6" data-testid="trivia-question">{q.question}</h3>

          {answered && !result ? (
            <div className="rounded-md border border-[#2A2A2A] bg-[#050505] p-6 text-center">
              <Trophy size={24} className="text-[#D4AF37] mx-auto mb-3" />
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]">Bereits beantwortet</div>
              <div className="text-sm text-[#A8A398] mt-2">Du hast heute schon geantwortet — komm morgen wieder für eine neue Frage.</div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {q.options.map((opt, i) => {
                const isSelected = selected === i;
                const isCorrect = result && i === result.correct_index;
                const isWrong = result && isSelected && !result.correct;
                const locked = selected !== null || answered;
                let cls = "border-[#2A2A2A] hover:border-[#D4AF37]/60 hover:bg-[#D4AF37]/5";
                if (isCorrect) cls = "border-[#8FBC8F] bg-[#8FBC8F]/10 text-[#B7E1B7]";
                else if (isWrong) cls = "border-[#C0392B] bg-[#C0392B]/10 text-[#EE9CA1]";
                else if (result && isSelected) cls = "border-[#575148] opacity-50";
                return (
                  <button key={i} onClick={() => submit(i)} disabled={locked}
                    data-testid={`trivia-option-${i}`}
                    className={`w-full text-left border rounded-md px-4 py-3 flex items-center gap-3 transitions-safe ${cls} ${!locked ? "cursor-pointer" : "cursor-default"}`}>
                    <span className="w-7 h-7 rounded-full border border-current flex items-center justify-center text-[11px] font-heading flex-shrink-0">
                      {isCorrect ? <Check size={14} /> : isWrong ? <X size={14} /> : String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-sm md:text-base">{opt}</span>
                  </button>
                );
              })}
            </div>
          )}

          {result && (
            <div className={`mt-5 rounded p-4 ${result.correct ? "bg-[#8FBC8F]/10 border border-[#8FBC8F]/40" : "bg-[#C0392B]/10 border border-[#C0392B]/40"}`} data-testid="trivia-result">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em]">
                {result.correct ? (
                  <><Check size={14} className="text-[#8FBC8F]" /> <span className="text-[#B7E1B7]">Richtig! {result.awarded ? `+${result.points} Punkte` : `${result.points} Punkte (nur für Mitglieder)`}</span></>
                ) : (
                  <><X size={14} className="text-[#EE9CA1]" /> <span className="text-[#EE9CA1]">Leider falsch</span></>
                )}
              </div>
              {result.explanation && <p className="mt-2 text-sm text-[#A8A398]">{result.explanation}</p>}
              {result.member_total_points !== null && result.member_total_points !== undefined && (
                <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[#EEDC82] flex items-center gap-1">
                  <Trophy size={11} /> Dein Punktestand: {result.member_total_points}
                </div>
              )}
              {!result.awarded && result.correct && (
                <div className="mt-3 text-xs text-[#A8A398]">
                  Als <Link to="/mitglied/login" className="text-[#D4AF37] underline">Movie-House-Club-Mitglied</Link> sicherst du dir Punkte für Rabatte und Prämien.
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
